import clsx from 'clsx';
import * as React from 'react';
import { HeadingContext } from 'react-aria-components';

import { Icon } from '@gitbook/spine-icons';
import { useEventCallback } from '@gitbook/util-react-hooks';

import { Button, type ButtonProps } from '../Button';
import { ListSkeleton } from '../List';
import { SidePanelExpandableContext } from './context';

export interface SidePanelProps {
    children: React.ReactNode;
    className?: string;
    /**
     * If true, the sidepanel can be expanded by the user to be larger.
     */
    expandable?: boolean;
    /**
     * Callback to call when the sidepanel is closed.
     */
    onClose?: () => void;
}

const SidePanelOnCloseContext = React.createContext<() => void>(() => {});

/**
 * Component to use to render a structured UI in a <PageWithSidePanel />.
 *
 * <SidePanel>
 *   <SidePanelHeader>
 *      <SidePanelHeaderMain>
 *        <SidePanelHeaderTitle>
 *          <Heading>Title</Heading>
 *        </SidePanelHeaderTitle>
 *        <SidePanelHeaderActions>
 *           <Button label="Action" />
 *           <SidePanelExpandButton />
 *           <SidePanelCloseButton />
 *        </SidePanelHeaderActions>
 *      </SidePanelHeaderMain>
 *      <SidePanelHeaderSecondary>
 *        ...
 *      </SidePanelHeaderSecondary>
 *   </SidePanelHeader>
 *   <SidePanelBody>
 *      <SidePanelBodyContent>
 *        ...
 *      </SidePanelBodyContent>
 *   </SidePanelBody>
 * </SidePanel>
 */
export function SidePanel(props: SidePanelProps): React.ReactElement {
    const { children, className, expandable = false, onClose = () => {} } = props;
    const { setExpanded } = React.useContext(SidePanelExpandableContext);

    React.useEffect(() => {
        if (!expandable) {
            setExpanded?.(false);
        }
    }, [expandable, setExpanded]);

    const onCloseStable = useEventCallback(onClose);

    return (
        <div className={clsx('flex h-full flex-1 flex-col', className)}>
            <SidePanelOnCloseContext.Provider value={onCloseStable}>
                {children}
            </SidePanelOnCloseContext.Provider>
        </div>
    );
}

export function SidePanelHeader(props: React.ComponentPropsWithoutRef<'div'>): React.ReactElement {
    const { children, className, ...rest } = props;

    return (
        <div {...rest} className={clsx('flex flex-col border-b', className)}>
            {children}
        </div>
    );
}

export function SidePanelHeaderMain(props: React.ComponentPropsWithoutRef<'div'>) {
    const { children, className, ...rest } = props;
    return (
        <div
            {...rest}
            className={clsx(
                '-mb-[1px] flex h-toolbar-header items-center gap-2 py-2 pr-3 pl-5',
                className
            )}
        >
            {children}
        </div>
    );
}

export function SidePanelHeaderSecondary(props: React.ComponentPropsWithoutRef<'div'>) {
    const { children, className, ...rest } = props;
    return (
        <div
            {...rest}
            className={clsx(
                'mb-2 flex h-8 w-full items-center justify-between gap-2 pr-3 pl-4',
                className
            )}
        >
            {children}
        </div>
    );
}

export function SidePanelHeaderTitle(props: React.ComponentPropsWithoutRef<'div'>) {
    const { children, className, ...rest } = props;

    return (
        <div {...rest} className={clsx('flex flex-1 flex-row gap-2', className)}>
            <HeadingContext.Provider value={{ level: 2, className: 'text-ui-heading-small' }}>
                {children}
            </HeadingContext.Provider>
        </div>
    );
}

export function SidePanelHeaderActions(
    props: React.ComponentPropsWithoutRef<'div'>
): React.ReactElement {
    const { children, className, ...rest } = props;

    return (
        <div {...rest} className={clsx('flex items-center gap-1', className)}>
            {children}
        </div>
    );
}

export type SidePanelBodyProps = React.ComponentPropsWithoutRef<'div'> & {
    /**
     * Fallback to render while the sidepanel is loading.
     */
    fallback?: React.ReactNode;
};

export function SidePanelBody(props: SidePanelBodyProps): React.ReactElement {
    const { children, className, fallback = <ListSkeleton className="p-4" />, ...rest } = props;

    return (
        <div {...rest} className={clsx('relative flex flex-1 flex-col overflow-y-auto', className)}>
            <React.Suspense fallback={fallback}>{children}</React.Suspense>
        </div>
    );
}

export function SidePanelBodyContent(
    props: React.ComponentPropsWithoutRef<'div'>
): React.ReactElement {
    const { children, className, ...rest } = props;
    return (
        <div {...rest} className={clsx('flex flex-col gap-4 p-5', className)}>
            {children}
        </div>
    );
}

/**
 * Button to close the sidepanel.
 */
export function SidePanelCloseButton(props: Partial<ButtonProps>) {
    const onClose = React.useContext(SidePanelOnCloseContext);

    return (
        <Button
            label="Close"
            leadingIcon={Icon.X}
            iconOnly
            kind="blank"
            {...props}
            onPress={onClose}
        />
    );
}

/**
 * Button to expoand the sidepanel.
 */
export function SidePanelExpandButton(props: Partial<ButtonProps>) {
    const { isExpanded, setExpanded } = React.useContext(SidePanelExpandableContext);

    return (
        <Button
            label={isExpanded ? 'Collapse panel' : 'Expand panel'}
            leadingIcon={isExpanded ? Icon.PanelLeftOpen : Icon.PanelLeftClose}
            iconOnly
            kind="blank"
            {...props}
            className={clsx(props.className, 'hidden md:block')}
            onPress={() => {
                setExpanded((prev) => !prev);
            }}
        />
    );
}
